"use client";

import { useEffect, useRef, useState, type ChangeEvent, type ClipboardEvent, type KeyboardEvent } from "react";

import { Button } from "@/components/ui/button";
import { BackLink } from "@/components/shared/back-link";
import { Emphasis, StepHeader } from "@/components/shared/step-header";
import { FieldHelp } from "@/components/shared/field";
import { onlyDigits } from "@/lib/strings";
import { maskEmail } from "@/lib/customers/mask-email";
import type { SixDigitCodeValues } from "@/lib/auth/code-schemas";

const RESEND_COOLDOWN_SECONDS = 60;

// Only the 2 lengths the backend actually issues — not generalized to N, YAGNI.
const CODE_LENGTH_WORDS: Record<number, string> = { 6: "seis", 8: "oito" };

export interface CustomerConfirmSignUpStepFormProps {
  email: string;
  isPending: boolean;
  errorMessage: string | null;
  onBack?: () => void;
  onSubmit: (values: SixDigitCodeValues) => void;
  onResend: () => void;
  isResending: boolean;
  /** Length of the code issued by Cognito for this flow — 6 (signup confirmation, default) or 8 (email login OTP). */
  codeLength?: number;
  /**
   * The CPF is already in the base: the prototype's "Cadastro reconhecido" voice, the code sent
   * to the e-mail on file.
   */
  known?: boolean;
  /** The back link's words — "voltar aos dados" by default, "corrigir o CPF" for a known CPF. */
  backLabel?: string;
}

/**
 * A grid of cells that submits itself as soon as all digits are filled —
 * no submit button. `codeLength` (default 6) covers signup confirmation;
 * the login step uses `CustomerLoginCodeStepForm` (8 digits) on top of this
 * same component.
 */
export function CustomerConfirmSignUpStepForm({
  email,
  isPending,
  errorMessage,
  onBack,
  onSubmit,
  onResend,
  isResending,
  codeLength = 6,
  known = false,
  backLabel = "voltar aos dados",
}: CustomerConfirmSignUpStepFormProps) {
  const [digits, setDigits] = useState<string[]>(() => Array(codeLength).fill(""));
  const [seconds, setSeconds] = useState(RESEND_COOLDOWN_SECONDS);
  const cellRefs = useRef<Array<HTMLInputElement | null>>([]);
  const lastSubmittedRef = useRef<string | null>(null);
  const codeLengthWord = CODE_LENGTH_WORDS[codeLength] ?? String(codeLength);

  useEffect(() => {
    cellRefs.current[0]?.focus();
  }, []);

  useEffect(() => {
    if (seconds <= 0) return undefined;
    const timer = setTimeout(() => setSeconds((value) => value - 1), 1000);
    return () => clearTimeout(timer);
  }, [seconds]);

  // Clears the cells on a refused code. The wizard nulls the error before each
  // attempt, so an identical message still re-triggers this.
  useEffect(() => {
    if (errorMessage) {
      setDigits(Array(codeLength).fill("")); // eslint-disable-line react-hooks/set-state-in-effect
      lastSubmittedRef.current = null;
      cellRefs.current[0]?.focus();
    }
  }, [errorMessage, codeLength]);

  function commit(next: string[]) {
    setDigits(next);
    if (next.every((digit) => digit !== "")) {
      const code = next.join("");
      // Guard: don't re-submit the same code that just failed.
      if (code === lastSubmittedRef.current) return;
      lastSubmittedRef.current = code;
      onSubmit({ code });
    }
  }

  function handleChange(index: number) {
    return (event: ChangeEvent<HTMLInputElement>) => {
      const digit = onlyDigits(event.target.value).slice(-1);
      const next = [...digits];
      next[index] = digit;
      commit(next);
      if (digit && index < codeLength - 1) cellRefs.current[index + 1]?.focus();
    };
  }

  function handleKeyDown(index: number) {
    return (event: KeyboardEvent<HTMLInputElement>) => {
      if (event.key === "Backspace" && !digits[index] && index > 0) cellRefs.current[index - 1]?.focus();
      if (event.key === "ArrowLeft" && index > 0) cellRefs.current[index - 1]?.focus();
      if (event.key === "ArrowRight" && index < codeLength - 1) cellRefs.current[index + 1]?.focus();
    };
  }

  function handlePaste(event: ClipboardEvent<HTMLDivElement>) {
    const pasted = onlyDigits(event.clipboardData.getData("text")).slice(0, codeLength);
    if (!pasted) return;
    event.preventDefault();
    const next = Array.from({ length: codeLength }, (_, index) => pasted[index] ?? "");
    commit(next);
    // Focus the first still-empty cell after a partial paste; the last cell
    // when the paste filled every digit.
    cellRefs.current[Math.min(pasted.length, codeLength - 1)]?.focus();
  }

  function handleResend() {
    setSeconds(RESEND_COOLDOWN_SECONDS);
    setDigits(Array(codeLength).fill(""));
    lastSubmittedRef.current = null;
    cellRefs.current[0]?.focus();
    onResend();
  }

  return (
    <div className="flex w-full flex-col">
      {onBack && <BackLink onClick={onBack}>{backLabel}</BackLink>}
      <StepHeader
        eyebrow={known ? "Cadastro reconhecido" : "Acesso seguro"}
        title={
          <>
            Confirme o seu <Emphasis>código</Emphasis>
          </>
        }
        /*
         * **Quem acabou de digitar o e-mail lê ele inteiro; quem foi reconhecido lê mascarado.**
         * (relato de 2026-10-15)
         *
         * A máscara existe para proteger um endereço que a pessoa **não** forneceu: no caminho
         * `known`, o CPF foi reconhecido e o sistema está contando para onde mandou o código —
         * mostrar o e-mail inteiro ali entregaria o endereço de outra pessoa a quem digitou um
         * CPF que não é o seu.
         *
         * No cadastro não há o que proteger: o e-mail acabou de ser digitado nesta mesma sessão,
         * dois passos atrás. Mascarar ali esconde justamente o que a pessoa precisa conferir — um
         * dígito trocado em `paulo` vira `pa***` e fica indistinguível do endereço certo. O
         * sintoma relatado é o de alguém reenviando o código para sempre, sem entender que o
         * código está indo para um endereço que não é o dela.
         */
        lede={
          known ? (
            <>
              Você já tem cadastro com a gente. Enviamos um código de {codeLengthWord} dígitos para o e-mail
              cadastrado, <strong data-clarity-mask="true" className="font-medium text-primary">{maskEmail(email)}</strong>. Digite-o abaixo
              para confirmar a sua identidade.
            </>
          ) : (
            <>
              Enviamos um código de {codeLengthWord} dígitos para{" "}
              <strong data-clarity-mask="true" className="font-medium text-primary">{email}</strong>. Digite-o abaixo para
              confirmar a sua identidade.
            </>
          )
        }
      />

      {/*
        The row must be able to shrink to any screen. An `<input>` brings its intrinsic width
        (about twenty characters) into the row's minimum size even under `min-w-0`, and eight of
        them held the whole step at its 440px cap on a 320–430px phone. `w-0` takes that
        contribution away and `flex-1` hands the width back evenly. The gap tightens on narrow
        screens so eight cells keep room for their digit.
      */}
      <div className="flex w-full min-w-0 gap-1.5 min-[400px]:gap-2.5" onPaste={handlePaste}>
        {digits.map((digit, index) => (
          <input
            data-clarity-mask="true"
            key={index}
            ref={(el) => {
              cellRefs.current[index] = el;
            }}
            className={`h-14 w-0 min-w-0 flex-1 rounded-sm border bg-background p-0 text-center font-mono text-[20px] text-foreground min-[400px]:h-16 min-[400px]:text-[24px] outline-none transition-colors duration-[var(--tb-duration-2)] ease-[var(--tb-ease-linen)] focus:border-primary focus:shadow-[0_0_0_3px_var(--tb-green-tint)] disabled:opacity-60 ${
              errorMessage ? "border-destructive" : "border-[color:var(--tb-border-strong)]"
            }`}
            inputMode="numeric"
            maxLength={1}
            value={digit}
            disabled={isPending}
            aria-label={`Dígito ${index + 1}`}
            onChange={handleChange(index)}
            onKeyDown={handleKeyDown(index)}
          />
        ))}
      </div>

      <div className="mt-3">
        <FieldHelp error={!!errorMessage}>{errorMessage}</FieldHelp>
      </div>

      <p className="mt-7 text-[12px] tracking-[0.06em] text-fg-muted">
        {seconds > 0 ? (
          <>
            Não recebeu? Você poderá reenviar em <strong className="font-medium">{seconds}s</strong>.
          </>
        ) : (
          <>
            Não recebeu?{" "}
            <Button
              type="button"
              variant="ghost"
              size="sm"
              className="h-auto w-fit gap-0 p-0 text-[12px] normal-case tracking-[0.06em] text-primary underline decoration-solid underline-offset-[3px] hover:bg-transparent hover:text-primary disabled:text-fg-subtle disabled:no-underline"
              disabled={isResending}
              onClick={handleResend}
            >
              Reenviar código
            </Button>
          </>
        )}
      </p>
    </div>
  );
}
